import React, { useState, useEffect } from 'react';
import SEO from '../../components/SEO';
import Breadcrumb from '../../components/Breadcrumb';

const SIPCalculator = () => {
  const [monthlyInvestment, setMonthlyInvestment] = useState('');
  const [expectedReturn, setExpectedReturn] = useState('');
  const [investmentPeriod, setInvestmentPeriod] = useState('');
  const [totalInvestment, setTotalInvestment] = useState(0);
  const [expectedValue, setExpectedValue] = useState(0);
  const [totalGains, setTotalGains] = useState(0);
  const [absoluteReturn, setAbsoluteReturn] = useState(0);

  const calculateSIP = () => {
    const P = parseFloat(monthlyInvestment) || 0;
    const r = parseFloat(expectedReturn) / 100 / 12; // Monthly return rate
    const n = parseFloat(investmentPeriod) * 12; // Total number of months

    if (P > 0 && r >= 0 && n > 0) {
      const totalInvestmentValue = P * n;
      const expectedValueValue = P * ((Math.pow(1 + r, n) - 1) / r) * (1 + r);
      const totalGainsValue = expectedValueValue - totalInvestmentValue;
      const absoluteReturnValue = (totalGainsValue / totalInvestmentValue) * 100;

      setTotalInvestment(Math.round(totalInvestmentValue));
      setExpectedValue(Math.round(expectedValueValue));
      setTotalGains(Math.round(totalGainsValue));
      setAbsoluteReturn(Math.round(absoluteReturnValue * 100) / 100);
    } else {
      setTotalInvestment(0);
      setExpectedValue(0);
      setTotalGains(0);
      setAbsoluteReturn(0);
    }
  };

  useEffect(() => {
    calculateSIP();
  }, [monthlyInvestment, expectedReturn, investmentPeriod]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const copyResult = () => {
    const resultText = `SIP Investment Calculation:
Monthly Investment: ${formatCurrency(parseFloat(monthlyInvestment) || 0)}
Expected Return: ${expectedReturn}% p.a.
Investment Period: ${investmentPeriod} years
Total Investment: ${formatCurrency(totalInvestment)}
Expected Value: ${formatCurrency(expectedValue)}
Total Gains: ${formatCurrency(totalGains)}
Absolute Return: ${absoluteReturn}%`;
    
    navigator.clipboard.writeText(resultText);
    alert('Results copied to clipboard!');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Investment & SIP Calculator – Plan Your Future"
        description="Plan your future with our SIP calculator. Calculate returns on systematic investment plans and mutual fund investments. Free SIP calculator for wealth building."
        keywords="SIP calculator, investment calculator, mutual funds, systematic investment plan, wealth building, SIP returns calculator, mutual fund calculator"
        type="webapplication"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Calculators', url: '/calculators' },
          { name: 'SIP Calculator', url: '/calculators/sip' }
        ]}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'SIP Calculator',
          applicationCategory: 'FinanceApplication',
          operatingSystem: 'Web',
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'INR'
          },
          description: 'Free online SIP calculator for calculating returns on systematic investment plans'
        }}
      />
      <Breadcrumb
        items={[
          { name: 'Home', url: '/' },
          { name: 'SIP Calculator', url: '/calculators/sip' }
        ]}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            SIP Investment Calculator
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Plan your future with systematic investment planning. Calculate potential returns on your SIP investments.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Calculator Form */}
          <div className="card">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">
              Investment Details
            </h2>
            
            <div className="space-y-6">
              {/* Monthly Investment */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Monthly SIP Amount
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₹</span>
                  <input
                    type="number"
                    value={monthlyInvestment}
                    onChange={(e) => setMonthlyInvestment(e.target.value)}
                    placeholder="Enter monthly SIP amount"
                    className="input-field pl-8"
                  />
                </div>
              </div>

              {/* Expected Return */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expected Annual Return (%)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    value={expectedReturn}
                    onChange={(e) => setExpectedReturn(e.target.value)}
                    placeholder="Enter expected return"
                    className="input-field pr-8"
                  />
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Historical equity returns: 12-15% p.a.
                </p>
              </div>

              {/* Investment Period */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Investment Period (Years)
                </label>
                <input
                  type="number"
                  value={investmentPeriod}
                  onChange={(e) => setInvestmentPeriod(e.target.value)}
                  placeholder="Enter investment period"
                  className="input-field"
                />
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="space-y-6">
            {/* Expected Value Result */}
            <div className="result-card">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Expected Value</h3>
                <div className="text-4xl font-bold text-green-600 mb-2">
                  {formatCurrency(expectedValue)}
                </div>
                <p className="text-sm text-gray-600">
                  After {investmentPeriod || 0} years
                </p>
              </div>
            </div>

            {/* Detailed Results */}
            <div className="card">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Investment Summary
              </h3>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-600">Total Investment</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(totalInvestment)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-600">Total Gains</span>
                  <span className="font-semibold text-green-600">
                    {formatCurrency(totalGains)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600 font-semibold">Absolute Return</span>
                  <span className="font-bold text-primary-600 text-lg">
                    {absoluteReturn}%
                  </span>
                </div>
              </div>

              <button
                onClick={copyResult}
                className="w-full mt-6 btn-primary"
              >
                Copy Results
              </button>
            </div>
          </div>
        </div>

        {/* Educational Content */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              💡 SIP Benefits
            </h3>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• Rupee cost averaging</li>
              <li>• Disciplined investing</li>
              <li>• Power of compounding</li>
              <li>• Start with small amounts</li>
            </ul>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              📈 Investment Tips
            </h3>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• Start early for maximum benefit</li>
              <li>• Increase SIP amount annually</li>
              <li>• Diversify across fund types</li>
              <li>• Stay invested for long term</li>
            </ul>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              🎯 SIP vs Lump Sum
            </h3>
            <p className="text-sm text-gray-600">
              SIP helps reduce market timing risk and provides better returns 
              in volatile markets through rupee cost averaging. It's ideal 
              for regular income earners.
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default SIPCalculator;
